import { useMemo, useState } from 'react'
import type { KeyboardEvent } from 'react'

import { ChevronDown, Search } from 'lucide-react'

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { DeliveryDto } from '@/services/deliveryService'
import type { PaperworkLimits } from '@/utils/paperworkSearch'
import { searchPaperwork } from '@/utils/paperworkSearch'
import {
  dayKeyOf,
  dayKeyOffset,
  toClockTime,
  toDayKey,
} from '@/utils/scheduleTime'

import './PaperworkSearch.css'

/** Matches FilterSelect's item treatment — same panel, same touch minimum. */
const ITEM_CLASS =
  'min-h-11 data-selected:bg-(--surface-selected) data-selected:text-(--blattner-dark-blue)'

/**
 * How much of a large result set to show, and when to stop holding back.
 *
 * The peek exists because a bare count cannot tell narrowing from
 * coincidence: typing a supplier's own number into this box can produce
 * hundreds of substring hits, and "202 matches" reads as progress where five
 * visible rows show instantly that none of them is the right truck.
 *
 * `listAllAt` is a human limit, not a rendering one — a few hundred rows would
 * render fine, but an unscannable list is what the count is protecting the
 * user from. Tune once Q2 (deliveries on a heavy day) has an answer.
 */
const LIMITS: PaperworkLimits = { peek: 5, listAllAt: 25 }

type Props = {
  /** The applied reference number. `undefined` means the filter is off. */
  value?: string
  onChange: (value: string | undefined) => void
  /**
   * Not the current view — a paperwork number is a precise query, and
   * answering it from a narrowed set is how a row goes missing. Excludes
   * terminal history from a previous day, though: no view ever renders
   * that, so surfacing it as a match would be a dead end (see the caller's
   * searchableDeliveries).
   */
  deliveries: readonly DeliveryDto[]
  /** The page's shared clock, for the Today / Tomorrow row labels. */
  now: Date
}

/**
 * Compact "when" for a result row.
 *
 * Deliberately shorter than `formatDayLabel`, which spells out the weekday and
 * month for a section heading that has the width for it. Here it shares a line
 * with a reference number.
 */
function whenLabel(scheduledFor: string, now: Date): string {
  const dayKey = toDayKey(scheduledFor)
  const clock = toClockTime(scheduledFor)

  if (dayKey === dayKeyOf(now)) return `Today ${clock}`
  if (dayKey === dayKeyOffset(now, 1)) return `Tomorrow ${clock}`

  const date = new Date(
    Number(dayKey.slice(0, 4)),
    Number(dayKey.slice(5, 7)) - 1,
    Number(dayKey.slice(8, 10)),
  )
  const short = date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  })
  return `${short} ${clock}`
}

/**
 * Find a delivery by the number on its paperwork.
 *
 * Shares the Popover + Command pairing with FilterSelect so it reads and
 * keyboards like the two pickers beside it, but it is not one of them:
 *
 * - **`shouldFilter={false}`.** cmdk filters only the items you render, so a
 *   panel showing 5 of 202 would have cmdk narrowing within those 5 and the
 *   count reporting nonsense. The match, the ordering and the slice are all
 *   owned here.
 * - **Rows are not bare strings.** A reference number identifies nothing on
 *   its own, so each row carries the carrier and the slot as well.
 * - **An empty query shows a prompt, not the list.** See searchPaperwork.
 *
 * Keeps the Search icon rather than borrowing one from the tile the way the
 * carrier and vendor pickers do. There is no paperwork icon on the tile to
 * borrow (the numbers render as mono pills beside the carrier and vendor
 * lines), and the magnifier is the honest signal: this control is the board's
 * search box wearing a filter's clothes.
 */
function PaperworkSearch({ value, onChange, deliveries, now }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const { candidates, total, truncated } = useMemo(
    () => searchPaperwork(deliveries, query, LIMITS),
    [deliveries, query],
  )

  const choose = (next: string | undefined) => {
    onChange(next)
    setQuery('')
    setOpen(false)
  }

  // A collapsed combobox is expected to open on ArrowDown; Radix's trigger is
  // a plain button, so it handles Enter and Space but not this.
  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' && !open) {
      event.preventDefault()
      setOpen(true)
    }
  }

  const searching = query.trim() !== ''

  /**
   * One line doing two jobs: the count the user is watching narrow, and the
   * only narrowing feedback a screen-reader user gets. `role="status"`
   * announces changes without stealing focus from the input being typed into.
   */
  const status = !searching
    ? `Searching ${deliveries.length} ${deliveries.length === 1 ? 'delivery' : 'deliveries'}`
    : total === 0
      ? 'No deliveries match'
      : truncated
        ? `${total} deliveries match — showing ${candidates.length}`
        : `${total} ${total === 1 ? 'delivery' : 'deliveries'} match`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={
          value
            ? 'filter-select__trigger filter-select__trigger--applied'
            : 'filter-select__trigger'
        }
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={value ? `BOL or PO: ${value}` : 'BOL or PO: any'}
        onKeyDown={handleTriggerKeyDown}
      >
        <Search aria-hidden="true" />
        <span className="filter-select__value">
          {value ?? 'BOL or PO number'}
        </span>
        <ChevronDown className="filter-select__chevron" aria-hidden="true" />
      </PopoverTrigger>

      <PopoverContent
        className="w-(--radix-popover-trigger-width) min-w-80 p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="BOL or PO number…"
          />

          <p className="paperwork-search__status" role="status">
            {status}
          </p>

          <CommandList>
            {/* Only when something is applied: with nothing to clear, the row
                would sit above the prompt competing with it. */}
            {value ? (
              <CommandItem
                forceMount
                className={`${ITEM_CLASS} text-(--fg3)`}
                value="__clear__"
                onSelect={() => choose(undefined)}
              >
                All deliveries
              </CommandItem>
            ) : null}

            {!searching ? (
              <p className="paperwork-search__hint">
                Type a BOL or PO number. Partial is fine — dashes and spaces are
                ignored.
              </p>
            ) : null}

            {/* Names the horizon rather than saying "no match". The board
                holds today plus thirty days, and a number the user *knows*
                exists getting a flat no is wrong in a way that discredits the
                control. */}
            {searching && total === 0 ? (
              <p className="paperwork-search__hint">
                Nothing on the board carries that number. The board covers today
                plus 30 days — a delivery further out is not here yet.
              </p>
            ) : null}

            {candidates.length > 0 ? (
              <CommandGroup>
                {candidates.map((candidate) => (
                  <CommandItem
                    key={candidate.delivery.id}
                    className={`${ITEM_CLASS} paperwork-search__row`}
                    value={candidate.delivery.id}
                    onSelect={() => choose(candidate.ref)}
                  >
                    <span className="paperwork-search__line">
                      <span className="paperwork-search__ref mono">
                        <span className="paperwork-search__kind">
                          {candidate.kind}
                        </span>
                        {candidate.ref}
                      </span>
                      <span className="paperwork-search__when">
                        {whenLabel(candidate.delivery.scheduledFor, now)}
                      </span>
                    </span>
                    <span className="paperwork-search__meta">
                      {candidate.delivery.carrier} ·{' '}
                      {candidate.delivery.material}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}

            {truncated ? (
              <p className="paperwork-search__hint">
                {total - candidates.length} more — keep typing to narrow.
              </p>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default PaperworkSearch
